import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User, Session } from '@supabase/supabase-js';
import { applyAllPreferences, getSavedDarkMode } from '@/hooks/useColorTheme';
import { hasTenantCustomTheme } from '@/contexts/TenantContext';
import { initAllSettingsFromDb } from '@/lib/settingsSync';

type AppRole = 'admin' | 'employee' | 'viewer' | 'driver' | 'customer' | 'super_admin';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  role: AppRole | null;
  mustChangeCredentials: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, name: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<AppRole | null>(null);
  const [mustChangeCredentialsState, setMustChangeCredentialsState] = useState(false);

  const fetchMustChange = async (userId: string) => {
    const { data } = await supabase
      .from('profiles')
      .select('must_change_credentials')
      .eq('id', userId)
      .maybeSingle();
    setMustChangeCredentialsState(!!data?.must_change_credentials);
  };

  const fetchRole = async (userId: string): Promise<AppRole | null> => {
    const { data } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .maybeSingle();
    const r = (data?.role as AppRole) || null;
    setRole(r);
    return r;
  };

  const checkCompanyActive = async (userId: string, userRole: AppRole | null): Promise<{ active: boolean; mismatch: boolean }> => {
    // Super admins bypass company check
    if (userRole === 'super_admin') return { active: true, mismatch: false };
    
    const { data: profile } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', userId)
      .maybeSingle();
    
    const userCompanyId = profile?.company_id ?? '';
    
    // Check if accessing from a company-specific subdomain
    const hostname = window.location.hostname;
    const baseDomain = 'deliveryjo.com';
    let tenantSubdomain: string | null = null;
    
    if (hostname.endsWith(baseDomain)) {
      const sub = hostname.replace(`.${baseDomain}`, '');
      if (sub && sub !== hostname && sub !== 'www' && sub !== 'admin' && sub !== 'super') {
        tenantSubdomain = sub;
      }
    }
    
    // If on a company subdomain, verify user belongs to that company
    if (tenantSubdomain) {
      const { data: tenantCompany } = await supabase
        .from('companies')
        .select('id, is_active')
        .eq('subdomain', tenantSubdomain)
        .maybeSingle();
      
      if (!tenantCompany || !tenantCompany.is_active) {
        return { active: false, mismatch: false };
      }
      if (userCompanyId !== tenantCompany.id) {
        return { active: true, mismatch: true };
      }
      return { active: true, mismatch: false };
    }
    
    // Default domain — just check company is active
    const { data } = await supabase
      .from('companies')
      .select('is_active')
      .eq('id', userCompanyId)
      .maybeSingle();
    return { active: data?.is_active !== false, mismatch: false };
  };

  const loadThemePrefs = async (userId: string) => {
    const { data } = await supabase
      .from('profiles')
      .select('color_theme, brightness, dark_mode')
      .eq('id', userId)
      .maybeSingle();
    if (data) {
      // If tenant has custom branding, only apply dark mode — don't overwrite company colors
      if (hasTenantCustomTheme()) {
        document.documentElement.classList.toggle('dark', data.dark_mode ?? false);
      } else {
        applyAllPreferences(
          data.color_theme || 'default',
          data.brightness ?? 50,
          data.dark_mode ?? false
        );
        document.documentElement.classList.toggle('dark', data.dark_mode ?? false);
      }
    }
  };

  const userIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    let isMounted = true;
    // Safety: never leave the UI stuck on the loading spinner forever.
    const safetyTimer = setTimeout(() => {
      if (isMounted) setLoading(false);
    }, 8000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;

        if (event === 'TOKEN_REFRESHED' ||
            (event === 'SIGNED_IN' && session?.user?.id === userIdRef.current)) {
          // Silent session refresh — don't trigger loading or re-render
          setSession(session);
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);
        userIdRef.current = session?.user?.id;

        if (event === 'SIGNED_IN') {
          setLoading(true);
          fetchRole(session!.user.id).then(async (r) => {
            // If the network is offline, do not pretend it's a permissions problem.
            if (typeof navigator !== 'undefined' && navigator.onLine === false) {
              if (isMounted) setLoading(false);
              return;
            }
            const { active, mismatch } = await checkCompanyActive(session!.user.id, r);
            if (!active || mismatch) {
              await supabase.auth.signOut();
              setUser(null);
              setSession(null);
              setRole(null);
              alert(mismatch ? 'هذا الحساب لا ينتمي لهذه الشركة.' : 'حسابك معطل. تواصل مع الإدارة.');
              if (isMounted) setLoading(false);
              return;
            }
            Promise.all([
              loadThemePrefs(session!.user.id),
              initAllSettingsFromDb(),
              fetchMustChange(session!.user.id),
            ]).catch(() => {/* don't block UI on prefs failure */});
          }).finally(() => {
            if (isMounted) setLoading(false);
          });
        } else if (event === 'SIGNED_OUT') {
          setRole(null);
          setMustChangeCredentialsState(false);
          setLoading(false);
        }
      }
    );

    // Check session validity when tab regains focus (detect deleted/banned users)
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && userIdRef.current) {
        // Skip the check when offline — getUser() would otherwise throw and trigger sign-out.
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
        try {
          const { data: { user: currentUser }, error } = await supabase.auth.getUser();
          if (error || !currentUser) {
            await supabase.auth.signOut();
          }
        } catch {
          // network blip — ignore, do not sign the user out
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initial load — await role before clearing loading
    const init = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted) return;
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            // Offline — keep the cached session but don't try server checks.
            return;
          }
          const r = await fetchRole(session.user.id);
          const { active, mismatch } = await checkCompanyActive(session.user.id, r);
          if (!active || mismatch) {
            await supabase.auth.signOut();
            return;
          }
          await Promise.all([
            loadThemePrefs(session.user.id),
            initAllSettingsFromDb(),
            fetchMustChange(session.user.id),
          ]).catch(() => {});
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    init();

    return () => {
      isMounted = false;
      clearTimeout(safetyTimer);
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signUp = async (email: string, password: string, name: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { name },
        emailRedirectTo: window.location.origin,
      },
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const mustChangeCredentials = mustChangeCredentialsState;

  return (
    <AuthContext.Provider value={{ user, session, loading, role, mustChangeCredentials, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
